import { useCallback } from 'react';
import { readOptions } from '../../../utils/variableOptions';
import { DYNAMIC_VARIABLE_DOCS, isDynamicVariable } from '../../../utils/dynamicVariables';
import { lookupVariable } from '../../../utils/variableDisplay';
import { lookupScopedVariable } from '../../../utils/variablePrecedence';
import { DYN_SUGGESTIONS_MAX_HEIGHT, DYN_SUGGESTIONS_MAX_WIDTH, NEWLINE, NEWLINE_ALL } from './syntaxHighlightHelpers';

/** Variable lookups, highlighted HTML, and plain-text / caret offsets of the contentEditable. */
export function useHighlightDom({
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
  collectionVariables,
  folderVariables,
  expandOnOverflow,
  setDynSuggestions,
  editableRef,
  isKvField,
}) {
  const positionDynSuggestions = useCallback((state, options = {}) => {
    if (!state?.items?.length) {
      setDynSuggestions(null);
      return;
    }
    let x = 0;
    let y = 0;
    const anchorToInput = options.anchorToInput ?? isKvField;
    if (anchorToInput && editableRef.current) {
      const er = editableRef.current.getBoundingClientRect();
      x = er.left;
      y = er.bottom + 4;
    } else {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const rect = sel.getRangeAt(0).getBoundingClientRect();
        if (rect.height > 0) {
          x = rect.left;
          y = rect.bottom + 4;
        } else if (editableRef.current) {
          const er = editableRef.current.getBoundingClientRect();
          x = er.left;
          y = er.bottom + 4;
        }
      } else if (editableRef.current) {
        const er = editableRef.current.getBoundingClientRect();
        x = er.left;
        y = er.bottom + 4;
      }
    }
    // Keep it on screen. The panel is `position: fixed` and was placed purely
    // relative to the field, so a field low in the window put the list below
    // the fold — and it must not run off the right edge either. Flipping above
    // the field is better than hanging off the bottom, since the field itself
    // is what the user is looking at.
    const margin = 8;
    const width = Math.min(DYN_SUGGESTIONS_MAX_WIDTH, window.innerWidth - margin * 2);
    x = Math.max(margin, Math.min(x, window.innerWidth - width - margin));
    if (y + DYN_SUGGESTIONS_MAX_HEIGHT > window.innerHeight - margin) {
      const above = (editableRef.current?.getBoundingClientRect().top ?? y) - DYN_SUGGESTIONS_MAX_HEIGHT - 4;
      y = above >= margin ? above : Math.max(margin, window.innerHeight - DYN_SUGGESTIONS_MAX_HEIGHT - margin);
    }

    setDynSuggestions({ ...state, x, y, active: state.active ?? 0 });
  }, [isKvField, editableRef, setDynSuggestions]);

  // Get variable value from environment or global variables
  /**
   * The alternatives a variable remembers, from whichever scope defines it.
   * Same precedence as getVariableValue; empty for an ordinary variable.
   */
  const findVariableOptions = useCallback((variableName) => {
    const hit = lookupScopedVariable(variableName, {
      folder: folderVariables,
      collection: collectionVariables,
      environment: selectedEnvironment?.variables,
      workspace: workspaceVariables,
      global: globalVariables,
    });
    return hit ? readOptions(hit.variable) : [];
  }, [selectedEnvironment, workspaceVariables, globalVariables, collectionVariables, folderVariables]);

  // `display` is what may be SHOWN (chip data-value, hover hint): a secret is
  // masked there (utils/variableDisplay). `value` is the real one, read only by
  // click-to-edit, which never puts it into the highlighted markup.
  const getVariableValue = useCallback((variableName) => lookupVariable(variableName, {
    folder: folderVariables,
    collection: collectionVariables,
    environment: selectedEnvironment?.variables,
    workspace: workspaceVariables,
    global: globalVariables,
  }), [selectedEnvironment, workspaceVariables, globalVariables, collectionVariables, folderVariables]);

  // Parse variables from text and create highlighted HTML
  const createHighlightedHTML = useCallback((text) => {
    if (!text) return '';
    
    const regex = /{{\s*([^}]+)\s*}}/g;
    let lastIndex = 0;
    let html = '';
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      const variableName = match[1].trim();
      const isDynamic = isDynamicVariable(variableName);
      const variableData = isDynamic
        ? { value: DYNAMIC_VARIABLE_DOCS[variableName] || 'Computed at request time', type: 'dynamic' }
        : getVariableValue(variableName);
      const exists = isDynamic || !!variableData;
      
      // Add text before variable
      html += escapeHtml(text.slice(lastIndex, match.index));
      
      const spanClass = exists
        ? `variable-span ${isDynamic ? 'variable-span--dynamic' : 'variable-span--exists'}`
        : 'variable-span variable-span--missing';

      // EVERY interpolation below is inside a quoted attribute that reaches
      // innerHTML, so every one of them is escaped — including the ones whose
      // values happen to be internally generated today. `variableName` is the
      // attacker-controlled one (it is captured by `[^}]+` from text that
      // arrives with any imported or shared collection), but leaving the others
      // raw just means the next person to widen one of them reopens this.
      // `exists`/`isDynamic` are booleans and `spanClass` is built from
      // literals; they are escaped anyway so the rule here is "no raw
      // interpolation", with no per-attribute exceptions to reason about.
      // Deliberately ONE line. The attributes used to be laid out across
      // several, which reads better in source and put literal newlines inside
      // the tag — they reach the clipboard's text/html flavour, and pasting
      // into anything that honours it produced a URL broken across lines.
      html += `<span class="${escapeHtml(spanClass)}" style="border-radius: 3px; padding: 1px 2px; cursor: ${isDynamic ? 'default' : 'pointer'};" data-variable="${escapeHtml(variableName)}" data-exists="${escapeHtml(exists)}" data-dynamic="${escapeHtml(isDynamic)}" data-value="${escapeHtml((variableData?.display ?? variableData?.value) || '')}" data-type="${escapeHtml(variableData?.type || '')}">${escapeHtml(match[0])}</span>`;
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    const remainingText = text.slice(lastIndex);
    html += escapeHtml(remainingText);
    
    return html;
  }, [getVariableValue]);

  // Helper function to escape HTML.
  //
  // This MUST escape quotes, and the previous `div.textContent -> div.innerHTML`
  // trick did not. Serializing a text node only escapes `&`, `<` and `>` —
  // quotes are escaped by the ATTRIBUTE serializer, which never ran here. So
  // every `attr="${escapeHtml(x)}"` below was still one `"` away from closing
  // its attribute and injecting `onerror=` into the span, and the result goes
  // straight to innerHTML in renderHighlightedContent.
  //
  // Escaping `"`/`'` in text content too is harmless (the parser turns them
  // back into the literal characters), so one helper covers both contexts and
  // there is no second, subtly-different "attribute version" to forget to call.
  const escapeHtml = (text) => String(text ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
  );

  // Get plain text from contenteditable div
  const getPlainText = useCallback((element) => {
    if (!element) return '';
    
    // Get text content while preserving the structure
    let text = '';
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let node = walker.nextNode();
    while (node) {
      text += node.textContent;
      node = walker.nextNode();
    }

    // Normalise the non-breaking space the BROWSER inserts.
    //
    // Editing a contentEditable around spaces makes Chromium/WebKit swap in
    // U+00A0 to stop the space collapsing visually — most visibly right after
    // a Backspace. It is a rendering artifact, but it was being read back as
    // part of the value, so a URL quietly acquired a character that is not a
    // space: it survives into the request and percent-encodes as %C2%A0.
    //
    // Not scoped to the URL field like the newline strip is, because unlike a
    // newline this is never something the user asked for — there is no way to
    // type one here, so every U+00A0 in this DOM came from the browser.
    return text.replace(/\u00a0/g, ' ');
  }, []);

  const getSelectionOffsets = useCallback(() => {
    if (!editableRef.current) return null;

    const selection = window.getSelection();
    if (!selection.rangeCount) return null;
    const range = selection.getRangeAt(0);

    if (
      !editableRef.current.contains(range.startContainer) ||
      !editableRef.current.contains(range.endContainer)
    ) {
      return null;
    }

    const startRange = range.cloneRange();
    startRange.selectNodeContents(editableRef.current);
    startRange.setEnd(range.startContainer, range.startOffset);

    const endRange = range.cloneRange();
    endRange.selectNodeContents(editableRef.current);
    endRange.setEnd(range.endContainer, range.endOffset);

    return {
      start: getPlainText(startRange.cloneContents()).length,
      end: getPlainText(endRange.cloneContents()).length
    };
  }, [getPlainText, editableRef]);

  /**
   * URL field: take browser-inserted newlines OUT OF THE DOM, not just out of the value.
   *
   * The expanded overlay is `white-space: pre-wrap`, and Chromium drops a filler newline after
   * the content when the caret has to sit at the very end behind an inline element — which is
   * exactly where "To Variable" leaves it when the token ends the URL. The value stayed clean
   * (handleInput/handleBlur strip newlines), but the field grew a visible empty second line and
   * the caret sat on it (owner, 21.09.2026). Nothing here can remove a newline the user meant:
   * this field never accepts one.
   */
  const stripDomNewlines = useCallback(() => {
    const node = editableRef.current;
    if (!expandOnOverflow || !node) return false;
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null, false);
    const dirty = [];
    for (let t = walker.nextNode(); t; t = walker.nextNode()) {
      if (NEWLINE.test(t.textContent)) dirty.push(t);
    }
    dirty.forEach((t) => { t.textContent = t.textContent.replace(NEWLINE_ALL, ''); });
    return dirty.length > 0;
  }, [expandOnOverflow, editableRef]);

  const setSelectionOffsets = useCallback((selectionOffsets) => {
    if (!editableRef.current) return;
    if (!selectionOffsets) return;

    const totalLength = getPlainText(editableRef.current).length;
    const clampedStart = Math.max(0, Math.min(selectionOffsets.start, totalLength));
    const clampedEnd = Math.max(clampedStart, Math.min(selectionOffsets.end, totalLength));

    const resolveNodeAtOffset = (targetOffset) => {
      let charCount = 0;
      const walker = document.createTreeWalker(
        editableRef.current,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );

      // `>` not `>=`: an offset landing exactly on a boundary between two text
      // nodes must resolve to the START of the next node, not the END of the
      // previous one. The highlighter wraps each `{{var}}` in its own span, so
      // the "previous node" at such a boundary is the text inside the variable
      // pill — resolving there puts the caret inside the variable and typing
      // extends the pill instead of the URL. `lastNode` keeps the true end of
      // the content reachable, since no node satisfies `>` there.
      let lastNode = null;
      let textNode = walker.nextNode();
      while (textNode) {
        const nodeLength = textNode.textContent.length;
        if (charCount + nodeLength > targetOffset) {
          return {
            node: textNode,
            offset: Math.min(targetOffset - charCount, nodeLength)
          };
        }
        charCount += nodeLength;
        lastNode = { node: textNode, offset: nodeLength };
        textNode = walker.nextNode();
      }

      if (lastNode) return lastNode;

      const range = document.createRange();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      return { node: range.endContainer, offset: range.endOffset };
    };

    const start = resolveNodeAtOffset(clampedStart);
    const end = resolveNodeAtOffset(clampedEnd);
    const range = document.createRange();
    const selection = window.getSelection();

    try {
      range.setStart(start.node, start.offset);
      range.setEnd(end.node, end.offset);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (e) {
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [getPlainText, editableRef]);

  /** The variables this field can see, for the suggestion list. */
  const suggestionScopes = useCallback(() => ({
    folderVariables,
    collectionVariables,
    environmentVariables: selectedEnvironment?.variables,
    workspaceVariables,
    globalVariables,
  }), [folderVariables, collectionVariables, selectedEnvironment, workspaceVariables, globalVariables]);

  return {
    positionDynSuggestions,
    findVariableOptions,
    getVariableValue,
    createHighlightedHTML,
    getPlainText,
    getSelectionOffsets,
    stripDomNewlines,
    setSelectionOffsets,
    suggestionScopes,
  };
}
